/**
 * «منشی هوشمند» — the record button at the foot of the doctor's sidebar (PM-0517),
 * the same place and the same states as the clinic panel's:
 *
 *   ready      the microphone, «منشی هوشمند» and F1 — press to record;
 *   recording  stop («توقف و اجرا»), the time, ×;
 *   processing the plain row with a spinner: the microphone is free again, and a
 *              press records the next command while this one finishes;
 *   warn       why nothing was recorded, for a few seconds.
 *
 * One height in every state, so nothing on the screen moves when it is pressed. On
 * the folded rail only the icon (or the stop button) stays.
 */
import React from 'react';
import { Mic, Square, X } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

import { clockOf } from './model';
import { useSecretaryRecorder } from './SecretaryHost';
import { useSecretaryStrings } from './secretaryStrings';

const SPIN_CSS = '@keyframes secretary-spin{to{transform:rotate(360deg)}}.secretary-spin{animation:secretary-spin .9s linear infinite}'
  + '@keyframes secretary-pulse{0%,100%{opacity:1}50%{opacity:.35}}.secretary-pulse{animation:secretary-pulse 1.2s ease-in-out infinite}'
  + '@media (prefers-reduced-motion: reduce){.secretary-spin,.secretary-pulse{animation:none}}';

/** Why the button is not recording, in the reader's words. */
export function warnText(warn, s) {
  if (!warn) return '';
  if (warn.kind === 'mic') return s(`secretary.recorder.warn.mic.${['denied', 'unsupported'].includes(warn.detail) ? warn.detail : 'error'}`);
  if (warn.kind === 'busy') return s('secretary.recorder.warn.busy');
  return s('secretary.recorder.warn.nothing');
}

function Kbd({ children }) {
  return (
    <kbd style={{
      fontFamily: 'inherit', fontSize: '0.6875rem', fontWeight: 700, padding: '1px 6px', borderRadius: 6,
      border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.75)', background: 'rgba(255,255,255,0.06)',
    }}
    >
      {children}
    </kbd>
  );
}

export default function SecretaryRecorder({ collapsed = false, iconCell = 48 }) {
  const c = useSecretaryRecorder();
  const s = useSecretaryStrings();
  const { formatDigits } = useTranslation();
  if (!c) return null;
  const box = { width: 'calc(100% - 20px)', margin: '6px 10px 8px', height: 44, boxSizing: 'border-box', flexShrink: 0 };
  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  if (c.recording) {
    return (
      <div
        data-testid="secretary-recording"
        style={{
          ...box, display: 'flex', alignItems: 'center', borderRadius: 12,
          background: 'rgba(255, 255, 255, 0.14)', border: `1px solid ${C.tealLight}`, color: 'white',
        }}
      >
        <style>{SPIN_CSS}</style>
        <span style={{ width: iconCell, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={c.stop}
            data-testid="secretary-stop"
            aria-label={s('secretary.recorder.stop')}
            title={s('secretary.recorder.stop')}
            aria-keyshortcuts="F1"
            style={{
              width: 32, height: 32, borderRadius: 999, border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
              background: C.tealLight, color: C.navy,
            }}
          >
            <Square size={13} fill="currentColor" aria-hidden="true" />
          </button>
        </span>
        <span style={{ ...fade, flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84375rem', fontWeight: 700 }}>
          <span aria-hidden="true" className="secretary-pulse" style={{ width: 8, height: 8, borderRadius: 999, background: C.red }} />
          <span aria-live="polite">{c.live ? s('secretary.recorder.recording') : s('secretary.recorder.processing')}</span>
          <span dir="ltr" style={{ marginInlineStart: 'auto', fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.8)' }} data-testid="secretary-clock">
            {formatDigits(clockOf(c.seconds))}
          </span>
        </span>
        <button
          type="button"
          onClick={c.discard}
          data-testid="secretary-discard"
          aria-label={s('secretary.recorder.discard')}
          title={s('secretary.recorder.discard')}
          aria-keyshortcuts="Escape"
          style={{
            ...fade, width: 30, height: 30, marginInline: 6, border: 0, borderRadius: 8, cursor: 'pointer',
            background: 'transparent', color: 'rgba(255,255,255,0.75)', display: 'grid', placeItems: 'center',
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }

  const state = c.warn ? 'warn' : c.pending > 0 ? 'processing' : 'idle';
  const label = state === 'warn' ? warnText(c.warn, s)
    : state === 'processing' ? s('secretary.recorder.processing') : s('secretary.navLabel');
  const icon = state === 'warn' ? (
    <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(255, 255, 255, 0.12)', color: C.amber, fontWeight: 800, fontSize: '0.875rem' }}>!</span>
  ) : state === 'processing' ? (
    <span
      aria-hidden="true"
      className="secretary-spin"
      style={{ width: 16, height: 16, borderRadius: 999, boxSizing: 'border-box', border: '2px solid rgba(255, 255, 255, 0.3)', borderTopColor: C.tealLight }}
    />
  ) : <Mic size={19} aria-hidden="true" />;
  return (
    <button
      type="button"
      data-testid="nav-secretary"
      data-state={state}
      onClick={c.start}
      title={collapsed ? label : s('secretary.recorder.record')}
      aria-label={state === 'idle' ? s('secretary.recorder.record') : `${label} · ${s('secretary.recorder.record')}`}
      aria-keyshortcuts="F1"
      style={{
        ...box,
        display: 'flex', alignItems: 'center', padding: 0, borderRadius: 12, cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.22)', background: 'rgba(255, 255, 255, 0.08)',
        color: 'white', fontFamily: 'var(--app-font)',
      }}
    >
      <style>{SPIN_CSS}</style>
      <span style={{ width: iconCell, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: state === 'warn' ? C.amber : C.tealLight }}>
        {icon}
      </span>
      <span
        aria-live="polite"
        style={{ ...fade, flex: 1, textAlign: 'start', fontSize: '0.875rem', fontWeight: 700, color: state === 'warn' ? C.amber : 'white' }}
      >
        {label}
      </span>
      <span style={{ ...fade, marginInlineEnd: 12, display: 'inline-flex' }}>
        <Kbd>F1</Kbd>
      </span>
    </button>
  );
}
